/**
 * The story in the Crystal Ballroom's spellbook (CrystalSpellbook.tsx):
 * Jessica Chase's "Portland on the Cusp of Spring".
 *
 * Each chapter is written out as it reads, a blank line between paragraphs.
 * A line of "* * *" is a break in the scene. A line that starts with "♪" is a
 * line of the song, set in violet italics; a "♪" with nothing after it is a
 * line still to be filled in, and isn't shown.
 *
 * The pages are cut from this when the book opens, by filling a page the
 * size of the open book until it's full (CrystalSpellbook.tsx), so nothing
 * here needs to know where a page ends.
 */

export const TITLE = "Portland on the Cusp of Spring";
export const AUTHOR = "Jessica Chase";

export interface Chapter {
  title: string;
  text: string;
}

export const CHAPTERS: Chapter[] = [
  {
    title: "The Train",
    text: `
It was blue in the morning, with no latent undertones of grey. A relief.

But it was too early. I woke up beside Maggie on the full-sized bed that was centered in the middle of the guest room at my Aunt's house, I’d slept closest to the wall-to-wall window that overlooked their acres of greenery. Its vibrant shades of green glowed like comic book slime or radioactive waste, and when I stayed there, its glare served as my alarm clock.

But it was also easy to wake up with Maggie because she was an insomniac. She always woke up before me; her eyes wide and robin's egg blue, unwashed mascara and eyeliner smeared about the corners like a raccoon, a mess on the pillow to match where she slept. The luminosity outside coupled with knowing we had a train to catch in under two hours propelled me from the bed, its heavy comforter crinkled like freshly fallen snow underneath boots, and the carpet felt plush underneath my feet. I loved to be there, and assume the life of someone more luxurious than myself. Maggie loved it too.

We started packing our things in the patch of warm sun slowly heating the small room. It made late winter feel like early spring. Outside the window that green backyard was covered in moss and clovers, bisected by a creek with a thin film of algae and lily pads on its surface, breaking to frame an occasional spot of sparkling murky water underneath. The yard went on until it met the forest of towering evergreens. There, at either side of the yard, there were paths leading into the woods. From the right angle, you could spot the window of their treehouse. Really, for most, it was not a yard, it was more like a painting. The kind of place where its ethereal features make you feel otherworldly, despite having done nothing out of the ordinary.

We left the room at a rate too leisurely for the amount of time we had to get to the train. It was 7:00 am and the train was scheduled to leave by 8:10 am. The metal spiral staircase shook as we went up its triangular steps to the main floor, to the ultra modern kitchen where we pushed the chrome buttons on the high tech espresso machine and made ourselves Americanos. With granola bars in our pockets we loudly rushed back downstairs.

Barely able to fasten shut our crammed bags, we left with shirtsleeves and pens poking out of our assorted bags still open zippers. By that time, the train’s arrival was supposed to coincide precisely with the phone's projected eta to the Tacoma train station, this heavied my foot on the gas, sure of the familiar route, I maneuvered Donna, my 1997 cherry-red Honda coup, at a speed that undoubtedly posed a sincere threat to the public.

Maggie and I didn’t speak during the drive. A message buzzed on the phone as I neared the station, to which we had arrived 10 minutes early. As we clumsily grabbed at our bags, impatient to both exit the vehicle and gather our things at once, I read the message. The train was delayed by 30 minutes.

In mid January I spent the $22.00 it took to buy a round trip Amtrak ticket to Portland. Maggie and I had bought tickets to see Angel Olsen in concert back in the fall, and since it was slightly more expensive than taking one of our cars, but took about the same amount of time to travel by train, we decided it would be much more pleasant not to drive.

The leather seats of the 508 Amtrak train were familiar and exciting. All of my trips on these trains to Portland had been replete with mixed emotions: excitement and love, jaw pain and dread, confusion and blind faith, that simply being in the coach heightened even my most mundane super-saver seat. On top of that nostalgia, the luxury of my Aunt’s house had a lingering effect of otherworldliness that stayed with me even after I had left it.

After we settled into our four-person pod, me by the window, Maggie at the aisle, faced two strangers across a table, I started on my makeup. Yellows, pinks and reds; a darker look but one that contrasted with my recently dyed metal blue-grey hair. The train had a coastal route that filled the car with white light that reflected with the ripples of the deep gray Pacific.
`,
  },
  {
    title: "The City",
    text: `
Three hours later we arrived at the PDX Union Station. Everything we had was fit into our backpack and crossbody purse getups. This was good and bad because even though it was incredibly heavy, it was the simplest way we had to carry the two bottles of wine we needed for each day ahead of us.

Being cheap and unafraid of long walks, we decided to trek the half hour from Union Station to Brooks' apartment. We were close to arrival much earlier than I had planned, so I changed our course and led us to Powell’s, a massive bookstore a few blocks from the apartment.

Inside the store, Maggie bought the book that I had been reading, No One Belongs Here More Than You by Miranda July. Per my suggestion, we assigned it to our Facebook book club.

Maggie had formed the book club online, held a meeting once, and no members including her nor I read more than a few pages of the first assigned book: Chronicles of a Death Foretold by Gabriel García Márquez. I had chosen the book blindly, because it was by the author of One Hundred Years of Solitude, one of my favorite books, but I still did not have the wherewithal to get through the first pages. July’s book was meant to replace the Márquez book, jive with our all-ladies members, and being comprised of short stories, easier to read at least some of, but since none of us were motivated and all of us were working students, the club quietly died.

In the cafe inside of Powell’s, people sat at wooden tables talking or reading. Maggie ordered a tea, and I followed suit. We sat, read and emptied the paper cups. We corrected this by filling them with wine in a clandestine maneuver behind our jackets. I took the plastic off a new notebook, wrote about the day, about Maggie, and the sunshine in the cafe.
`,
  },
  {
    title: "The Apartment",
    text: `
By the time the afternoon rolled around, Maggie and I made our way to the apartment. I’m here, I texted Brooks. After a few minutes, he replied that he would be right down. Then he was there, beside his best friend Joe, with whom he shared a studio apartment. Sometimes, when I would come to stay with him before, I would hope that Joe would be around. He never was, and I would feel a tinge of disappointment, partially because I liked Joe, we had a lot in common, and never crossed paths enough to be friends when we had all gone to school together. But mostly it was because Joe was good, and I liked the way Brooks was when he was around. He was social enough to curb Brooks’ intense introversion.

They were alike in a few ways, they were musicians, both blonde, but Joe’s was nearer to brown, and cropped short. Brooks had a paler toned blonde that reached to his shoulders, and he stood a few inches taller than Joe. It was rare to spot him without a serious demeanor. As soon as I saw them, a feeling of relief washed over me, not unlike the feeling I had a year and a half before then, the first time I had seen Brooks on the very same doorstep on which he stood now.

“Hi,” I said, smiling, obviously staring at Brooks, who smiled but turned to Joe. The mood was light, likely because of Joe, who was as kind and lively as I had always remembered.

“Hi!” said Joe.

“Hi,” said Brooks, matching my more inquisitive tone than Joe’s bright one.

Maggie stood there next to me.

“This is Maggie,” I said.

Another chorus of Hi ensued. She was bashful, smiling at the two and avoiding eye contact. In a manner similar to my own, Joe led the conversation.

“How was the trip?”

“We took the train and walked to Powell’s and read our books,” I replied without a breath, gesturing to where my book was stuffed into my bag as if they could see it, “it was sunny.”

It was still sunny. The only detail I had forgotten to mention was that the sky was blue.

“Nice!” said Joe, to my relief. He and I were the talkative ones. Brooks was reserved, pensive, and the few that knew him, knew him to be that way. When he did speak, it was eloquent. After a conversation, found in rarity, it became obvious that he was profoundly intelligent.

“We are going to Chuck’s tonight,” he said, looking at me. Chuck studied at Reed College, across the Willamette River about a twenty minute drive from the apartment in Goose Hollow. Once, Brooks and I visited him there to go to a dance. It was around Thanksgiving break, but since we were all from the area, we had stayed that weekend, and the campus was quiet.

* * *

Standing nearly a foot taller than me, Chuck looked down to greet us at the sliding door of his dorm’s basement floor and introduced us to Maria, his girlfriend. They led us to the common area on his floor and we sat on couches.

“Would either of you like something to drink?” he asked us hospitably, listing a few options. "The wine, I mean, it's an eight-dollar bottle, but.. and know I sound like an asshole saying this, but if you let the wine breathe," he said, rolling his eyes, "it has a surprisingly good taste." We all laughed. Chuck was smart, and had many opinions that came out even when discussing beverages.

Brooks requested juice, and after hesitantly looking at him for approval, I met his gaze long enough to discern genuine acceptance to feel I could request wine without later reprimand. Brooks had been uncomfortable with me drinking in the past, and knowing that he did not drink presently, I held precaution with drinking, but I was twenty years old, and I wanted wine.

Chuck produced a bottle from a cabinet that I was disproportionately excited to see had a painted label I recognized from museums in Colombia, it unmistakably that of Fernando Botero, whom I had loved as soon as I had seen the collection of art he gifted to the public at a museum in Bogotá as a child. I thanked Chuck, drank the wine, and was surprised at how good it tasted. I wanted more, but said nothing, content with the company and my small victory.

We walked beneath the tall trees and the fall brush crunched under our shoes. As we approached the hall, music floated through the ornate windows of the old university Gothic structure. A pop song reflected off of its stone walls, and over the dark hardwood floors. The room was large, and thus looked somewhat empty with only a few people dancing in the middle, next to a projector screen painted with footage of Selena.

Throughout the night, Chuck expressed his distaste for Reedies, which shone sharply as he mocked the dancers. Our group stood in the corner. Brooks and I looked out at the small crowd, and swayed subtly, locked in a short radius with our group at the periphery. We left shortly after we got there. Brooks didn’t like Chuck’s cynicism, he whispered in my ear, he wanted to dance. It sent chills through me.

We both knew this was important, because before, at our own school dance, he refused to stand with me and we had watched from a table as my friends twirled around freely. They were sad for us, and even approached, grabbing our arms to pull us in, but he stayed firmly planted, my hand in his. There in that moment, in the dusk of the room, I felt Brooks was at last himself, the kind of person that wanted to dance, and even stood up for those who did it shamelessly in the empty room. I squeezed his hand, enormously happy, and agreed, I did too.

After we left, our group of dance-deserters made way to the library, another Tudor Gothic building that upon descending into the basement, turned an ugly 90s U.S. modern. We wandered through its halls, making jokes and pulling books from the shelf.

* * *

“And we are going to stay there tonight,” he finished, looking at Joe and then at me.

“Here are the keys,” he extended two silver keys on a simple metal ring, holding them between his index, middle finger and thumb. Our hands did not touch, and before I knew it, the keys were in my palm.

“You’re not coming back tonight?” I asked with my arm still outstretched holding the keys, as if he might take them back, turn around and lead us up to the apartment.

“No, I don’t think so,” he said in a tone that sounded completely certain that they would not. Audibly resistant to this news, I tried to conceal my disappointment.

“Okay,” I said, looking down at the cold metal, I paused.

“Thank you so much for letting us stay with you still.”

Still.

Still letting us stay with you, after having—albeit miserably disinclined—broken up with you that past January.

“No problem,” Brooks said, Joe echoing the sentiment with a nod.

Though his lips were slightly pursed, and every word maintained poise, I could feel the hum of his excitement, just as I felt my own. In the spark in his eye, I saw myself and my own eye bursting.

“Okay,” I said again, my best effort to prolong our already expired time together. Moving a bit to the left, Joe smiled and tactfully said, “Have fun at the show,” to which I responded, “You too.”

As they left, Maggie and I approached the building entrance, my eyes unfocused but fixed forward as though I would turn into a pillar of salt if I dared look back. The anxiety abated with each step, and there were a few steps, which we climbed, four flights up.

I turned the key in the door, and as soon as I pushed it open, the smell hit us. It was awful, like the place was made of old shoes and gym clothes. Maggie did a cartoonish face of horror and I made one right back at her, both of us bursting with laughter as I rushed forward to the window and threw it open. I spun to the right and sped over to the kitchen, nearly throwing my back out trying to pry open the other window. Without thinking much, we started to clean.

Right before four o’clock, we went to the nearby chain grocery store with the intention of buying something to make for dinner, but left instead with a bag of mandarin oranges, energy drinks, yerba mate, granola bars, a stolen pastrami sandwich and three more bottles of wine. As a sign of good will and thanks, I climbed onto the large built in Murphy dresser bed that hid Joe’s bed in the wall and put a bottle of wine in its empty rack fixture for Joe. I walked to the kitchen with the bags and put energy drinks I knew Brooks liked in the fridge.

Angel Olsen was touring her new album, MY WOMAN, which I had first played through a few months before, there in the Portland apartment, laying next to Brooks. Since the Crystal Ballroom was only a few blocks down Burnside, we had ample time, and since we had no desire to go anywhere, we drank.

One by one, each of us nearly a full bottle in, we climbed out onto the fire escape that overlooked the downtown. I brought two instant cameras, and we took film photos of each other, which I forbade her from posting online. Brooks had already dismissed my attempts to get him to sit out on the fire escape with me, citing "building rules". It was not in my nature to respect authority, and finally defying it then was my quiet rebellion. The late winter sunset began to paint the city buildings around us hues of blue and purple. Now too cold, we climbed back in.

Inside the apartment, eating oranges and roaming around the small studio, I opened the walk-in closet turned office and spotted a legal pad with Brooks’ ink handwriting on the first page, open on his desk. I read the page, it made little sense, and as I lifted the pad to see more, I stopped. With staunch, uncharacteristic determination, I resisted the urge to sift through his notebooks full of writing. Ostensibly drained of willpower from this, but totally full of wine, I decided to put on his clothes.

Somewhere, partially decomposed and floating next to a lone red Solo Cup in the Great Pacific garbage patch, there exists a film photograph of myself wearing nothing but a black lace bra with nude roses embroidered on the corners and Brooks’ baby blue boxer briefs.
`,
  },
  {
    title: "The Crystal Ballroom",
    text: `
Doors at 7. Or 8?

“That’s the opener,” Maggie responded from the floor, polishing off her bottle and skinning another mandarin orange, “I don’t really care though, we don’t have to be on time, but I mean we could go now,” she trailed off, looking outside at the twilight that had almost completely faded into a common moonlit night.

I looked at the phone.

“Chris Cohen opens and I heard he’s good, so if we can make it, I’d like to see him.”

From my splayed position on the deluxe air mattress in the corner of the room where Brooks slept since moving in at the end of August, I dared Maggie to finish the last of my bottle. She obliged.

We left and made our way down King Ave, and during the turn onto West Burnside Street, we broke into a sprint, slamming our feet into the concrete sidewalk, until we reached the lights that hung outside the venue. As we laughingly sorted our windblown hair and tried to catch our breath, we found the end of the line. People stood next to the side of the building in a talkative murmur. Within a few moments, a man wearing a dark coat in front of us began to walk forward, and so did I, following the rest of the absent denizens funneling into the heavy doors.

At my side, Maggie was rifling through her purse and pulled out a phone. With a click, she unlocked and stared at it, her face fully relaxed as if almost asleep.

We approached the door and Maggie, purse clutched to her chest, teetered toward the wall, lurching forward suddenly, until regaining her balance and upright disposition with her long hair wild in her face.

Her purse dropped, but caught on her arm jarring it downward as she pushed the phone out toward the bouncer, it was an ungraceful but understandable gesture given the circumstances. The woman asked for her I.D., and with a bit of fumbling in the purse again, she handed it over, confused. It was an all ages show.

“Have you been drinking,” the bouncer asked without the upward inflection of a question.

“No.”

“Give me your hands,” she said in monotone.

“Why? I’m 21!”

“You’ve clearly been drinking, now give me your hands or you’re going to leave.”

The woman gestured with a black flat tip marker in one hand, but with the same tone I’ve heard cops use while holding a gun in bystander footage of domestic policing power trips.

Maggie reluctantly extended one of her hands, and the woman swiftly took it, drew a big X, grabbed her other hand, and drew a second one. Before releasing her, she looked at Maggie.

“Don’t wash it off.”

Up the stairs, we found a bathroom, and began scrubbing the X marks off in the sink. It took vigorous effort, but luckily when we heard music from the ballroom, we had gotten her hands clean. Inside, I felt the drunk beginning to wear off, though we both had more wine than I’d ever drank in a day, it was steadily and over the course of many hours.

The 21+ area didn't have any good vantage point, so we joined the general audience. Five songs in, I was bored and unmoved by Chris Cohen’s set, only made worse by the fact that I was suddenly sober. Next to me, Maggie was rifling through her purse again, I nudged her.

“Want to go up to the bar for a drink?”

Maggie looked up from her purse.

“Do you have my phone?” she replied. I didn’t.

Angel went on, and we jumped excitedly, she played “Hi-five,” my favorite of hers to yell while driving. Maggie and I sang it with her, looking at each other, banging our heads up and down.

Then came “Shut Up and Kiss Me”, a song we would later learn to play together, up in the treehouse at Maggie’s Seattle neighborhood backyard that April. After singing there, she would drop me off at Key Arena so I could meet Brooks to see Radiohead play on his 21st birthday. We agreed to go together even after breaking up, but none of the best seats had been sold together, so we’d settled on seats in different sections.

Spin magazine published an article a few days before the show that announced for the first time in ten years, Radiohead had been playing the song “Where I End and You Begin” in their set. When I first went to Portland, Brooks had told me that was the song that had been playing in my room when we lost our virginities to each other at 17 years old, before we graduated and he moved to New York. At the time, I was too focused on other things to notice the song, but I laughed when he told me, knowing Radiohead had been on in the background.

“Wait, did you plan that?” I asked.

“I don’t think so,” he replied, “but I wouldn’t put it past 17 year-old Brooks.”

I sent him a screenshot of the article before the show.

“I can’t wait to tell the person next to me that I lost my virginity to this song,” he replied.

We met outside and headed to a spot in between our two sections, stood and talked until we heard a sound from inside. With a shared wide-eyed look, we spun in opposite directions, and bolted to our seats. It was the last time I saw him.

They didn’t play the song. The next day Seattle’s magazine The Stranger published an article the following Monday titled: “Radiohead Was So Goddamn Good on Saturday Night That Life Briefly Seemed Tolerable Again.”

* * *

Maggie nudged me again after a few more songs.

“I can’t find my phone,” she said into her purse, her head hardly visible.

Annoyed, but unable to ignore her distress enough to enjoy the show, I began inspecting the floor under the beam of the phone's flashlight. Who cares about stupid phones, I thought. When I noticed her shoes were no longer next to mine, I looked up and saw that she had disappeared into the crowd.

♪

I turned to the stage in surprise that this quiet tune made the setlist. I was pleased, I loved the song so much that I even played it live as a part of my set when I performed around Tacoma.

Sometimes I think that only people who have experienced a real and intense romantic love can write songs like this. Not because they are better than those who haven't, but because they must do it

♪

They don't have much of a choice, the sentiment requires a certain implicit expression

♪

♪

♪

♪

♪

When I sang it, I would think of Brooks, and I’d imagine myself finally able to open a window that let such bright light in that it warmed through to our blood, all of the dark vanishing. Like the morning light when it spilled into the Portland apartment, it would wake me, and Brooks would be there fast asleep. It was all I wanted,

♪

When he would wake, it was the purest time of day, and I would see the light from the open window make a halo around him, and I knew he was happy

♪

I swayed with the first year and a half together, now a part of my skin, the silence of the subsequent year and a half apart and its burst into the last year together, touched only just by the ringing of the rain in January. Listening to the song there, as I stood with my feet sore, alone and sober, I thought of the window in the morning by the bed.

The song ended.

Maggie slammed into me.

“I went and asked the front about my phone and they wanted to see the Xs on my hands so I ran away,” she said, bumping into the people around us like a pinball.

I put my hand on Maggie's arm.

“I’ll go ask for you and say that you left or something,” I said, using her arm for momentum to push myself forward, and her out of my way to create a path through the crowd.

On the main floor, I asked about the phone.

“We have her phone but your friend needs to come get it herself,” said the woman, perched on a tall stool. I protested, fabricating some reason why she couldn’t, but the woman just sat there like a gargoyle.

The phone colored the night, not just our quest to find Maggie’s, but also my own constant checking to see about Brooks, maybe a text had come. I had wanted to watch the rest of the show in peace, but Maggie’s worried whispers and my tired feet brought me out of it before it was over and we left the crowd.

“Well,” I began as the lights came up, “what if we switched clothes and you went and asked for it but dressed as me?”

Enthused by this plan, we bolted back to the pink bathroom and tore our clothes off in a whirlwind. We tossed them over and under the stall, while I explained why this was not only a very good plan, but honestly genius, they would never recognize you, I said, just act different, and here, use my I.D.. Also, put this lipstick on.

In the thinning crowd, I walked out first, and tried to make it seem as though I was Maggie, my face turned, sporting her mini skirt. Feeling satisfied with my performance, I waited outside on the corner facing the doors until she came out to meet me.

Ten minutes of me checking the phone passed, when she blew past me and said, “Let’s. Go.”
`,
  },
  {
    title: "That Night",
    text: `
The front door creaked open and let yellow light from the hall into the apartment. I woke up, with Maggie sunken into Brooks’ bed next to me. I didn’t remember falling asleep, just lying on the bed with the phone in my hand. It must have been early, around 11 o’clock, because when Joe and Brooks stepped in, it was just after 2 in the morning.

“Hey!” said Joe, in a forced whisper turned laugh.

“Oh, hi!” I said, surprised and giddy, still washed with the euphoria of having just woken up.

They closed the door, and then the room was lit only by the city outside. The night came back to me.

“Maggie got 86’d from the Crystal Ballroom!” I exclaimed.

All of us devolved into laughter, Maggie propped herself up.

“What!” laughed Brooks.

“Yeah! Maggie got eighty-sixed from the Crystal Ballroom!” I said again. I had just learned that term, 86’d, and loved using it.

“So, she’s not allowed back.”

“How did that happen?” Brooks asked, still chuckling.

“Well,” I paused, “we ran all the way there at full speed, and Maggie tripped nearly onto the bouncer.”

Maggie chimed in with a protest about the stumble, it was the phone being very stuck in the bag, she insisted.

“Then she accused her of being drunk, drew big Xs on her hands and told us not to wash them off. So, we promptly washed them off, of course, and it turns out Maggie left her phone in the bathroom.”

Maggie told them about the phone being returned, going back and forth with the bouncer, and a blacklight revealing that we had washed off the Xs. She got up and perched over her things on the rug.

“That’s when we decided to switch clothes like in Scooby Doo,” I said, “but she recognized Maggie anyways.” We all laughed, Brooks and Joe taking off their shoes and jackets.

“Y’know,” said Joe, “this reminds me of this podcast you should check out—Brooks told me you listen to radio a lot.” The conversation turned to one about public radio and podcasts, which I loved.

Brooks walked through the main room into the small tiled kitchen and opened the fridge.

“Oh!” he said, eyeing the cans I’d bought, “Did you get these?”

“Yes! They’re for you," I turned toward the living room, "and Joe, there’s wine on the rack there.”

“Ah, nice!” said Joe, with an open mouthed smile, and turned to the rack. "Thank you, that's awesome," his voice muffled into the drawer underneath the rack as he looked for something. He slid it shut and stepped carefully over the mess to get to the bathroom.

In the small apartment there was silence, behind me I sensed that Maggie was facing the wall, either back asleep or pretending to be.

“There are two Yerba Mates in there for us tomorrow, but all of the rest are yours,” I said to Brooks.

He pulled the water pitcher from the fridge and shut it.

“You’re an angel.”

I sat up

* * *

“It’s your bed,” I said quietly to Brooks, “we could all fit.”

“That’s alright,” he said.

“I could ask her to sleep on the floor with that blanket.”

He emptied the glass of water in a single drink.

“We could sleep on the floor, but really, I mean, there’s room.”

“I’m fine,” he said, plugging the phone into the wall, “I like the floor.”
`,
  },
];

/** a page of the book: the title page, the contents, or a run of a chapter */
export type Page =
  | { kind: "title" }
  | { kind: "contents" }
  | { kind: "text"; chapter: number; heading?: string; paragraphs: string[] };

export const BREAK = "* * *";
const words = (s: string) => s.split(/\s+/).filter(Boolean).length;

/** the paragraphs of a chapter, without the song lines still to be filled in */
export function paragraphsOf(text: string) {
  return text
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter((p) => p && p !== "♪");
}

/** a paragraph's sentences, each with the space after it */
export const sentencesOf = (p: string) => p.match(/[^.!?]+[.!?]+["”’]?\s*|[^.!?]+$/g) ?? [p];

/** a paragraph too long for a page, cut between its sentences */
function cut(p: string, budget: number): string[] {
  if (words(p) <= budget) return [p];
  const sentences = sentencesOf(p);
  const out: string[] = [];
  let now = "";
  for (const s of sentences) {
    if (now && words(now + s) > budget) {
      out.push(now.trim());
      now = "";
    }
    now += s;
  }
  if (now.trim()) out.push(now.trim());
  return out;
}

/**
 * The whole book as pages, about `budget` words to a page (a first guess,
 * used until the open book has been measured): the title page,
 * the contents, then each chapter starting on a fresh page under its title.
 * A scene break never starts or ends a page.
 */
export function pagesOf(budget: number): Page[] {
  const pages: Page[] = [{ kind: "title" }, { kind: "contents" }];
  CHAPTERS.forEach((ch, chapter) => {
    let page: { kind: "text"; chapter: number; heading?: string; paragraphs: string[] } = { kind: "text", chapter, heading: ch.title, paragraphs: [] };
    let used = 6;   // the heading takes some of the first page
    const flush = () => {
      while (page.paragraphs[page.paragraphs.length - 1] === BREAK) page.paragraphs.pop();
      if (page.paragraphs.length) pages.push(page);
      page = { kind: "text", chapter, paragraphs: [] };
      used = 0;
    };
    for (const whole of paragraphsOf(ch.text)) {
      for (const p of cut(whole, budget)) {
        const n = words(p);
        if (used && used + n > budget) flush();
        if (p === BREAK && !page.paragraphs.length) continue;
        page.paragraphs.push(p);
        used += p === BREAK ? 3 : n + 4;   // a little for the gap between paragraphs
      }
    }
    flush();
  });
  return pages;
}

/** the page each chapter starts on, for the contents */
export function chapterStarts(pages: Page[]) {
  return CHAPTERS.map((_, c) => pages.findIndex((p) => p.kind === "text" && p.chapter === c));
}
